import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import {
  createChallenge,
  deleteChallenge,
  getChallengeDailyGuide,
  getChallengeDailyGuideImages,
  getChallengeDetail,
  getChallengeParticipants,
  getChallengeSchedule,
  getChallenges,
  updateChallenge,
  createChallengeDailyGuide,
  updateChallengeDailyGuide,
  deleteChallengeDailyGuide,
  assignChallengeTeams,
  updateParticipantTeam,
  getChallengeTeams,
  createChallengeTeams,
  deleteChallengeTeam,
  grantChallengeParticipantsShield,
} from './challenges.api';
import type {
  AssignChallengeTeamsParams,
  CreateChallengePayload,
  CreateChallengeTeamsParams,
  CreateChallengeDailyGuideRequest,
  UpdateChallengePayload,
  UpdateChallengeDailyGuideRequest,
  DeleteChallengeDailyGuideParams,
  DeleteChallengeTeamParams,
  GetChallengeParticipantsParams,
  GetChallengesParams,
  GrantChallengeParticipantsShieldParams,
  UpdateParticipantTeamParams,
} from './challenges.api';

const CHALLENGES_STALE_TIME = 1000 * 60; // 1 minute
const CHALLENGES_GC_TIME = 1000 * 60 * 5; // 5 minutes

export const challengesQueries = {
  all: ['challenges'] as const,

  list: (params: GetChallengesParams = {}) =>
    queryOptions({
      queryKey: ['challenges', params] as const,
      queryFn: () => getChallenges(params),
      placeholderData: keepPreviousData,
      staleTime: CHALLENGES_STALE_TIME,
      gcTime: CHALLENGES_GC_TIME,
    }),

  detail: (challengeId: number) =>
    queryOptions({
      queryKey: ['challenges', 'detail', challengeId] as const,
      queryFn: () => getChallengeDetail(challengeId),
      staleTime: CHALLENGES_STALE_TIME,
      gcTime: CHALLENGES_GC_TIME,
    }),

  schedule: (challengeId: number) =>
    queryOptions({
      queryKey: ['challenges', 'schedule', challengeId] as const,
      queryFn: () => getChallengeSchedule(challengeId),
      staleTime: CHALLENGES_STALE_TIME,
      gcTime: CHALLENGES_GC_TIME,
    }),

  dailyGuideImages: (challengeId: number) =>
    queryOptions({
      queryKey: ['challenges', 'daily-guide-images', challengeId] as const,
      queryFn: () => getChallengeDailyGuideImages(challengeId),
      staleTime: CHALLENGES_STALE_TIME,
      gcTime: CHALLENGES_GC_TIME,
    }),

  dailyGuide: (challengeId: number, dayIndex: number) =>
    queryOptions({
      queryKey: ['challenges', 'daily-guide', challengeId, dayIndex] as const,
      queryFn: () => getChallengeDailyGuide({ challengeId, dayIndex }),
      staleTime: CHALLENGES_STALE_TIME,
      gcTime: CHALLENGES_GC_TIME,
    }),

  participants: (challengeId: number, params: GetChallengeParticipantsParams) =>
    queryOptions({
      queryKey: ['challenges', 'participants', challengeId, params] as const,
      queryFn: () => getChallengeParticipants({ challengeId, params }),
      placeholderData: keepPreviousData,
      staleTime: CHALLENGES_STALE_TIME,
      gcTime: CHALLENGES_GC_TIME,
    }),
  teams: (challengeId: number) =>
    queryOptions({
      queryKey: ['challenges', 'teams', challengeId] as const,
      queryFn: () => getChallengeTeams(challengeId),
      staleTime: CHALLENGES_STALE_TIME,
      gcTime: CHALLENGES_GC_TIME,
    }),
  mutation: {
    create: () => ({
      mutationFn: (payload: CreateChallengePayload) => createChallenge(payload),
    }),
    update: () => ({
      mutationFn: ({
        challengeId,
        payload,
      }: {
        challengeId: number;
        payload: UpdateChallengePayload;
      }) => updateChallenge({ challengeId, payload }),
    }),
    delete: () => ({
      mutationFn: (challengeId: number) => deleteChallenge(challengeId),
    }),
    createDailyGuide: () => ({
      mutationFn: ({
        challengeId,
        image,
        request,
      }: {
        challengeId: number;
        image?: File;
        request: CreateChallengeDailyGuideRequest;
      }) => createChallengeDailyGuide({ challengeId, image, request }),
    }),
    updateDailyGuide: () => ({
      mutationFn: ({
        challengeId,
        guideId,
        image,
        request,
      }: {
        challengeId: number;
        guideId: number;
        image?: File;
        request: UpdateChallengeDailyGuideRequest;
      }) => updateChallengeDailyGuide({ challengeId, guideId, image, request }),
    }),
    deleteDailyGuide: () => ({
      mutationFn: (params: DeleteChallengeDailyGuideParams) =>
        deleteChallengeDailyGuide(params),
    }),
    assignTeams: () => ({
      mutationFn: (params: AssignChallengeTeamsParams) =>
        assignChallengeTeams(params),
    }),
    updateParticipantTeam: () => ({
      mutationFn: (params: UpdateParticipantTeamParams) =>
        updateParticipantTeam(params),
    }),
    createTeams: () => ({
      mutationFn: (params: CreateChallengeTeamsParams) =>
        createChallengeTeams(params),
    }),
    deleteTeam: () => ({
      mutationFn: (params: DeleteChallengeTeamParams) =>
        deleteChallengeTeam(params),
    }),
    grantParticipantsShield: () => ({
      mutationFn: (params: GrantChallengeParticipantsShieldParams) =>
        grantChallengeParticipantsShield(params),
    }),
  },
};
